import { FormProvider, useForm } from "react-hook-form";

import styles from "./styles.module.scss";
import utilStyles from "../../../BuildProgram/utilStyles.module.scss";
import FormInput from "../../../../components/Programs/FormInput";
import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { RootState } from "typedefs";
import { shallowEqual, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import InfoModal from "app/routes/Calendar/InfoModal";
import useActions from "app/utils/hooks/useActions";
import { fetchAcademySetup } from "app/actions/academySetup";
import {
  isSetupItemOpen,
  showOfferingCreatedToast,
} from "app/components/AcademySetup/SetupToast";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import { useHistory, useLocation } from "react-router-dom";
import SaveBar from "app/routes/Sessions/SaveBar";
import Label from "app/components/Programs/Label";
import SelectBox from "app/components/OptionButtonGroup/SelectBox";
import {
  bill_start_options,
  billingCyclesRule,
  currency,
  currency_symbol,
  pricing_unit_options,
} from "../utils";
import {
  schedulingFetchGet,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import Navbar from "app/components/Navbar";
import BookingModal from "../SinglePayment/BookingModal";
import ManualTrackingSection from "../SinglePayment/ManualTrackingSection";
import AutomaticTrackingSection from "../SinglePayment/AutomaticTrackingSection";
import { UserContext } from "app/contexts/UserContext";
import useFacilityLessonRates from "app/routes/Sessions/hooks/useFacilityLessonRates";
import PaymentMethodSelector from "app/components/PaymentMethodSelector";
import RestrictUsage, { RestrictUsageRef } from "../RestrictUsage";
import SubscriptionStartFields, {
  billStartOptionFor,
  customStartDateError,
  subscriptionStartPayload,
} from "../SubscriptionStart";
import useCurrentCoachProfile from "app/utils/hooks/scheduling/useCurrentCoachProfile";

const Subscription = () => {
  const { t } = useTranslation();
  // Check for facility context from URL
  const urlParams = new URLSearchParams(window.location.search);
  const facilityId = urlParams.get('facilityId');
  const mapObjectToDefaultValues = (product) => {
    let tracking = "none";
    let number_of_sessions = 1;
    let unlimited = false;

    const rateLinks = product.rateLinks || [];

    if (rateLinks.length > 0) {
      if (rateLinks[0].title) {
        tracking = "automatic";
      } else {
        tracking = "manual";
        number_of_sessions = rateLinks[0].quantity ?? 1;
        unlimited = rateLinks[0].quantity === -1;
      }
    }
    return {
      title: `[Copy of] ${product.title}`,
      description: product.description,
      number_of_sessions,
      unlimited,
      price: product.price.toString(),
      past_due: product.pastDue,
      bill_start: billStartOptionFor(product.billStartDate),
      // CN-2832: a copied custom date may have passed; the form asks again.
      billing_start_on: product.billingStartOn ?? null,
      carry_over_sessions: !!product.carryOverSessions,
      pricing_time: product.pricingTime || product.recurring || 1,
      pricing_unit:
        pricing_unit_options.find(
          (option) => option.value === product.pricingUnit
        ) || pricing_unit_options[0],
      currency_value:
        currency.find((option) => option.value === product.currency) ||
        currency[0],
      tracking,
      max_billing_cycles: product.maxBillingCycles ?? null,
    };
  };

  const user = useContext(UserContext);
  const { data: coachProfile } = useCurrentCoachProfile();

  const contextCurrency = facilityId
    ? user.facility?.currency
    : coachProfile?.currency;
  const contextStripeEnabled = facilityId
    ? user.facility?.stripeIntegrationEnabled
    : coachProfile?.stripeIntegrationEnabled;

  const initialCurrency =
    currency.find((option) => option.value === contextCurrency?.toUpperCase()) ||
    currency[0];
  const initialStripeEnabled = contextStripeEnabled ?? true;

  const methods = useForm({
    defaultValues: {
      title: "",
      description: "",
      number_of_sessions: 1,
      unlimited: false,
      price: null,
      past_due: 10,
      bill_start: bill_start_options[0],
      billing_start_on: null,
      carry_over_sessions: false,
      pricing_time: 1,
      pricing_unit: { value: 0, label: "Month(s)" },
      currency_value: initialCurrency,
      tracking: "automatic",
      max_billing_cycles: null,
    },
  });

  // `collectType` is the payment guard's choice, seeding the payment method.
  const location = useLocation<{
    product?: any;
    collectType?: "online" | "offline";
  }>();
  const { setValue, watch, getValues, register } = methods;
  // const unlimited = watch("unlimited");
  const past_due = watch("past_due");
  const currency_value = watch("currency_value");
  const pricing_unit = watch("pricing_unit");
  const history = useHistory();
  const userId = useSelector((state: RootState) => state.user.self.id);
  const self = useSelector((state: RootState) => state.user.self, shallowEqual);
  // The checklist and its analytics belong to the Academy account's coach;
  // facility staff (admins, schedulers, other coaches) use the same form
  // without either.
  const isAcademyCoach = isAcademyOnboardingCoach(self);
  const academySetupActions = useActions({ fetchAcademySetup });
  /**
   * Whether the subscription being created is the one that completes the
   * checklist's packages_subscriptions item. Captured at submit time — the
   * alert's own gate only asks whether the item is complete now, so reading
   * it after the create would re-celebrate on every later offering.
   */
  const completesAcademySetupItem = useRef(false);
  const [showPopup, setShowPopup] = useState(false);
  const [productMeta, setProductMeta] = useState<{ tax_code_label?: string, stripe_dashboard_url?: string } | null>(null);
  const [errors, setErrors] = useState([]);
  const navigateBack = () => {
    history.goBack();
  };
  const [loading, setLoading] = useState(false);
  const [bookingOptions, setBookingOptions] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [lessonRates, setLessonRates] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const facilityBookingEnabled = user.facility?.featureFlags?.facilityLevelBookingOptions === true;
  const { rates: facilityRates } = useFacilityLessonRates(
    user.facility?.id,
    facilityBookingEnabled
  );

  // Payment method state
  const [collectOnline, setCollectOnline] = useState(initialStripeEnabled);
  const [collectOffline, setCollectOffline] = useState(!initialStripeEnabled);
  const [onlineDefault, setOnlineDefault] = useState(initialStripeEnabled);
  const [stripeIntegrationEnabled, setStripeIntegrationEnabled] = useState(initialStripeEnabled);
  // The state above starts from an optimistic default; analytics wait for the
  // fetched value so a direct visit never records stripe_connected: true for
  // an unconnected coach.
  const [stripeStatusResolved, setStripeStatusResolved] = useState(false);

  // Ref to RestrictUsage component for reading values at submit time
  const restrictUsageRef = useRef<RestrictUsageRef>(null);
  const schedulingInterval = user.facility?.featureFlags?.schedulingInterval as number | undefined;

  // Derived value for API
  const isOnlineDefault =
    (collectOnline && !collectOffline) ||
    (collectOnline && collectOffline && onlineDefault);

  useEffect(() => {
    if (location.state?.product) {
      const defaultValues = mapObjectToDefaultValues(location.state.product);
      methods.reset(defaultValues);
      if (
        defaultValues.tracking === "automatic" &&
        Array.isArray(location.state.product.rateLinks)
      ) {
        const bookingOptionsFromClone = location.state.product.rateLinks.map((rateLink) => ({
          id: rateLink.id,
          title: rateLink.title,
          duration: rateLink.duration,
          category: rateLink.category || "",
          quantity: rateLink.quantity == null ? -1 : rateLink.quantity,
        }));
        setBookingOptions(bookingOptionsFromClone);
      }

      const clonedCollectType = location.state.product.collectType;
      if (clonedCollectType === "offline") {
        setCollectOnline(false);
        setCollectOffline(true);
        setOnlineDefault(false);
      } else if (clonedCollectType === "online") {
        setCollectOnline(true);
        setCollectOffline(false);
        setOnlineDefault(true);
      } else if (clonedCollectType === "both") {
        setCollectOnline(true);
        setCollectOffline(true);
        setOnlineDefault(location.state.product.onlineDefault ?? true);
      }
    }
    // The payment guard's answer wins over a cloned product's method: the
    // guard asked because the method is permanent for the new object.
    if (location.state?.collectType) {
      const online = location.state.collectType === "online";
      setCollectOnline(online);
      setCollectOffline(!online);
      setOnlineDefault(online);
    }
  }, [location.state, methods]);

  // const handleEnableUnlimited = (checked: boolean) => {
  //   setValue("unlimited", checked);
  // };

  // Fetch Stripe status, payment method defaults, and currency
  useEffect(() => {
    const fetchProfileData = facilityId
      ? schedulingFetchGet(`facility`)
      : schedulingFetchGet(`coach_profiles/${userId}`);

    fetchProfileData
      .then((data) => {
        const hasStripe = data.stripeIntegrationEnabled || false;
        setStripeIntegrationEnabled(hasStripe);
        setStripeStatusResolved(true);

        if (!hasStripe) {
          setCollectOnline(false);
          setCollectOffline(true);
          setOnlineDefault(false);
        } else if (!location.state?.product && !location.state?.collectType) {
          setCollectOnline(true);
          setCollectOffline(false);
          setOnlineDefault(true);
        }

        if (!location.state?.product) {
          setValue(
            "currency_value",
            currency.find((option) => option.value === data.currency?.toUpperCase()) ||
            currency[0]
          );
        }
      })
      .catch((error) => {
        console.error("Failed to load profile data", error);
        // Unknown status is treated as unconnected: online collection can
        // never be switched off later, so the safe default is offline only.
        setStripeIntegrationEnabled(false);
        setCollectOnline(false);
        setCollectOffline(true);
        setOnlineDefault(false);
        setStripeStatusResolved(true);
      });
  }, [userId, facilityId]);

  useEffect(() => {
    if (facilityBookingEnabled) {
      return;
    }
    schedulingFetchGet(`coach_profiles/${userId}/pricing_groups`)
      .then((data) => {
        const rates = data[0]?.lessonRates || [];
        const filteredRates = rates.filter(rate => rate.active);
        setLessonRates(filteredRates);
      })
      .catch((error) => {
        console.error("Failed to load lesson rates", error);
      });
  }, [userId, facilityBookingEnabled]);

  const displayRates = useMemo(() => {
    if (facilityBookingEnabled) {
      return facilityRates.filter(rate => rate.active);
    }
    return lessonRates;
  }, [facilityBookingEnabled, facilityRates, lessonRates]);

  // const unlimited_toggle = () => {
  //   return (
  //     <SwitchToggle
  //       defaultChecked={unlimited}
  //       title={t("Unlimited")}
  //       name="enableUnlimited"
  //       defaultValue={false}
  //       setValue={setValue}
  //       onChange={handleEnableUnlimited}
  //     />
  //   );
  // };

  const toPayloadQty = (q: number | undefined | null) =>
    q === -1 ? null : Math.max(1, (q ?? 1));

  const onSubmit = async () => {
    if (loading) {
      return;
    }
    setSubmitAttempted(true);

    if (tracking === "automatic" && bookingOptions.length === 0) {
      return;
    }
    setLoading(true);
    setSubmitAttempted(false);

    const {
      number_of_sessions,
      pricing_unit,
      max_billing_cycles,
      bill_start: billStartValue,
      billing_start_on,
      carry_over_sessions,
      ...rest
    } = getValues();

    // CN-2832: this form submits without react-hook-form's validation, so the
    // custom start date is checked here as well.
    const startError = customStartDateError(billStartValue, billing_start_on, t, rest.pricing_time);
    if (startError) {
      methods.setError("billing_start_on", { message: startError });
      setLoading(false);
      return;
    }

    const collectType = collectOnline && collectOffline ? "both" :
      collectOnline ? "online" : "offline";

    // Validate and get time restriction values
    if (facilityId && isAutomaticTracking && restrictUsageRef.current) {
      const validationError = restrictUsageRef.current.validate();
      if (validationError) {
        setLoading(false);
        return;
      }
    }

    const restrictionValues = restrictUsageRef.current?.getValues();

    const productData = {
      ...rest,
      ...(facilityId ? {} : { coach_id: userId }),
      past_due,
      ...subscriptionStartPayload({ bill_start: billStartValue, billing_start_on, carry_over_sessions }),
      type: "subscription",
      currency: currency_value.value,
      collect_type: collectType,
      online_default: isOnlineDefault,
      ...(facilityId && isAutomaticTracking && restrictionValues ? {
        restrict_weekdays: restrictionValues.restrictWeekdays,
        restrict_weekends: restrictionValues.restrictWeekends,
        ...(restrictionValues.restrictWeekdays ? {
          weekday_begin_at: restrictionValues.weekdayBeginAt,
          weekday_end_at: restrictionValues.weekdayEndAt,
        } : {}),
      } : {}),
      pricing_unit: pricing_unit.value === 0 ? 'months' : 'hours',
      // CN-2641: optional cycle cap — empty means the subscription renews
      // indefinitely (null on the API).
      max_billing_cycles: max_billing_cycles ? Number(max_billing_cycles) : null,
      rate_links:
        tracking === "manual"
          ? [{
            quantity: getValues().unlimited ? null : number_of_sessions
          }]
          : bookingOptions.map((option) => ({
            lesson_rate_id: option.id,
            quantity: toPayloadQty(option.quantity),
          })),
    };

    const apiEndpoint = facilityId
      ? `/facilities/${facilityId}/stripe_products`
      : `/stripe_products`;

    // Coach-scoped offerings only: the checklist reads
    // `stripe_products?coach_id=`, which facility-level products never reach.
    completesAcademySetupItem.current =
      !facilityId &&
      isAcademyCoach &&
      isSetupItemOpen("packages_subscriptions");

    try {
      const data = await schedulingFetchPost(apiEndpoint, productData);
      if (!data || data.error) {
        throw new Error(data.error || "Error creating product");
      }
      setProductMeta({ tax_code_label: data.taxCodeLabel, stripe_dashboard_url: data.stripeDashboardUrl });
      setShowPopup(true);
    } catch (error) {
      console.error("Error creating product:", error);
      setErrors(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The "Offering Created" modal carries the Stripe tax category and
   * dashboard link, so the setup alert waits for it rather than replacing it:
   * the coach reads the modal, dismisses it, and the alert greets them on
   * /billing. It refetches the checklist and stays silent unless the API
   * confirms the item.
   */
  const dismissOfferingCreated = () => {
    history.push(facilityId ? "/facility_billing" : "/billing");
    if (!completesAcademySetupItem.current) {
      return;
    }
    completesAcademySetupItem.current = false;
    showOfferingCreatedToast(academySetupActions.fetchAcademySetup, (path, state) =>
history.push(path, state)
    );
  };

  const titleValue = watch("title", "");
  const tracking = watch("tracking");
  const isAutomaticTracking = tracking === "automatic";

  return (
    <div>
      <Navbar label={t("Create Subscription")} hasSearch={false} hasNotifications={false} onBackClick={navigateBack} />
      <div className={styles.empty_container}>
        <div
          className={`${styles.container} ${styles.coach_min_height} billing-page-container`}
        >
          <FormProvider {...methods}>
            <form id="singlePaymentForm">
              <div className={styles.row}>
                <div
                  className={`${utilStyles.column} ${styles.general_section}`}
                >
                  <label>{t("General")}</label>
                  <div className={styles.horizontal_line} />
                  <div className={styles.column}>
                    <div className={styles.input_container_counter}>
                      <FormInput
                        tagTitle={t("Title")}
                        fieldName="title"
                        placeholder={t("Enter package name")}
                        isRequired={true}
                        {...register("title", {
                          required: t("This field is required"),
                          validate: {
                            maxLength: (value) =>
                              value.length <= 250 ||
                              t("The title must not exceed 250 characters"),
                          },
                        })}
                        maxLength={250}
                      />
                      <div className={styles.character_counter}>
                        {titleValue?.length}/250
                      </div>

                      <div className={styles.textarea_width}>
                        <Label
                          text={t("Description")}
                          item={(
                            <textarea
                              data-testid="description"
                              placeholder={t("Enter a description")}
                              {...methods.register("description")}
                              className={styles.details_textarea}
                            />
                          )}
                          containerStyle={styles.mt_16}
                        />
                      </div>

                      <div className={styles.tracking_container}>
                        <div className={styles.tracking_radio_group}>
                          <label className={styles.tracking_radio}>
                            <input
                              type="radio"
                              value="automatic"
                              {...register("tracking")}
                            />
                            <span className={styles.normal_text}>{t("Automatic Tracking")}</span>
                          </label>
                          <label className={styles.tracking_radio}>
                            <input
                              type="radio"
                              value="manual"
                              {...register("tracking", {
                                onChange: () => setSubmitAttempted(false),
                              })}
                            />
                            <span className={styles.normal_text}>{t("Manual Tracking")}</span>
                          </label>
                          <label className={styles.tracking_radio}>
                            <input
                              type="radio"
                              value="none"
                              {...register("tracking", {
                                onChange: () => setSubmitAttempted(false),
                              })}
                            />
                            <span className={styles.normal_text}>{t("Without Tracking")}</span>
                          </label>
                        </div>
                      </div>

                      {tracking === "manual" && (
                        <ManualTrackingSection />
                      )}

                      {tracking === "automatic" && (
                        <>
                          <AutomaticTrackingSection
                            bookingOptions={bookingOptions}
                            setBookingOptions={setBookingOptions}
                            setShowBookingModal={setShowBookingModal}
                            isPackage={false}
                            submitAttempted={submitAttempted}
                          />
                        </>
                      )}

                    </div>
                  </div>
                </div>
                <div
                  className={`${utilStyles.column} ${styles.billing_section}`}
                >
                  <label>{t("Billing")}</label>
                  <div className={styles.horizontal_line} />
                  <div className={styles.billing_content}>
                    <div className={styles.billing_field}>
                      <Label
                        text={t("Currency")}
                        item={(
                          <SelectBox
                            options={currency}
                            value={currency_value}
                            setValue={(value) =>
                              setValue("currency_value", value)
                            }
                            customWidth="100%"
                            customControl={{
                              borderRadius: "8px",
                            }}
                          />
                        )}
                      />
                    </div>
                    <div className={styles.billing_field}>
                      <FormInput
                        tagTitle={t("Price")}
                        fieldName="price"
                        isRequired={true}
                        addDollar={true}
                        placeholder={"0"}
                        currency={{
                          currency: currency_symbol[currency_value.value],
                        }}
                        {...register("price", {
                          required: t("This field is required"),
                          validate: {
                            isNumber: (value) => {
                              if (currency_value.value === "JPY") {
                                return Number.isInteger(Number(value)) || t("The value must be a whole number for JPY");
                              } else {
                                return !isNaN(value) || t("The value must be a number");
                              }
                            }
                          },
                        })}
                      />
                    </div>
                    <div className={`${styles.billing_field} ${styles.row_multiple_elems}`}>
                      <div className={styles.pricing_item}>
                        <FormInput
                          tagTitle={t("Every")}
                          fieldName="pricing_time"
                          isRequired={true}
                          {...register("pricing_time", {
                            required: t("This field is required"),
                            validate: {
                              isNumber: (value) =>
                                (value !== null &&
                                  Number.isInteger(Number(value))) ||
                                t("The value must be a whole number"),
                              minValue: (value) =>
                                value >= 1 ||
                                t("The value must be at least 1"),
                            },
                          })}
                        />
                      </div>
                      {pricing_unit_options.length > 1 ? (
                        <div className={`${styles.pricing_item} ${styles.mt_24}`}>
                          <SelectBox
                            options={pricing_unit_options.map((opt) => ({ value: opt.value, label: t(opt.label) }))}
                            value={pricing_unit}
                            setValue={(value) => setValue("pricing_unit", value)}
                            customWidth="100%"
                            customControl={{
                              borderRadius: "8px",
                            }}
                          />
                        </div>
                      ) : (
                        <div className={`${styles.pricing_item} ${styles.mt_24}`}>
                          <span style={{ padding: "8px 0", display: "inline-block" }}>{t(pricing_unit_options[0]?.label || "Month(s)")}</span>
                        </div>
                      )}
                    </div>
                    <div className={styles.billing_field}>
                      <SubscriptionStartFields />
                    </div>
                    <div className={styles.billing_field}>
                      <FormInput
                        tagTitle={t("Past Due")}
                        fieldName="past_due"
                        tooltipText={t(
                          "The number of days after an invoice due date that a payment can remain unpaid before additional actions, such as late fees or service suspension, are taken."
                        )}
                        tooltipColor={"gray"}
                        isRequired={true}
                        {...register("past_due", {
                          required: t("This field is required"),
                          validate: {
                            isNumber: (value) =>
                              (value !== null &&
                                Number.isInteger(Number(value))) ||
                            t("The value must be a whole number"),
                          inRange: (value) =>
                            (value >= 1 && value <= 30) ||
                            t("The value must be between 1 and 30"),
                        },
                      })}
                      />
                    </div>
                    {/* Reads "Auto Renewal Ends After [ 3 ] Billing Cycles".
                        The unit rides inside the field's row rather than in a
                        second column beside it: this panel is 366px, and at half
                        of that the label broke after "Ends". */}
                    <div className={styles.billing_field}>
                      <FormInput
                        tagTitle={t("Auto Renewal Ends After")}
                        fieldName="max_billing_cycles"
                        unit={t("Billing Cycles")}
                        tooltipText={t(
                          "Ends the subscription after this many billing cycles. Leave empty to renew until cancelled."
                        )}
                        tooltipColor={"gray"}
                        placeholder={t("Unlimited")}
                        tooltipPlacement="bottom"
                        // The label is long, so the icon sits far right in this
                        // column — open the bubble leftwards to stay inside it.
                        tooltipOrientation="right"
                        tooltipMaxWidth={200}
                        validate={billingCyclesRule(t)}
                      />
                    </div>
                    <PaymentMethodSelector
                      collectOnline={collectOnline}
                      setCollectOnline={setCollectOnline}
                      collectOffline={collectOffline}
                      setCollectOffline={setCollectOffline}
                      onlineDefault={onlineDefault}
                      setOnlineDefault={setOnlineDefault}
                      forceOnlineDefault={true}
                      disableOnline={!stripeIntegrationEnabled}
                      disabledReason={!stripeIntegrationEnabled ? t("Requires active Stripe integration") : undefined}
                      showInfoBanner
                      // Academy onboarding analytics only, as on the program
                      // forms. The payment guard already reported the field
                      // for a form it seeded; only a direct visit reports it
                      // here, once the Stripe status has actually been fetched.
                      tracking={
                        !isAcademyCoach ||
                        location.state?.collectType ||
                        !stripeStatusResolved
                          ? undefined
                          : { objectType: "subscription", stripeConnected: stripeIntegrationEnabled }
                      }
                    />
                    {facilityId && (
                      <RestrictUsage
                        ref={restrictUsageRef}
                        isAutomaticTracking={isAutomaticTracking}
                        schedulingInterval={schedulingInterval}
                        offeringType="subscription"
                      />
                    )}
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
        <SaveBar
          onSubmit={methods.handleSubmit(onSubmit)}
          submitLabel={t("Save")}
          // The payment method starts from an optimistic default; the form
          // cannot be saved until the real Stripe status has landed.
          submitDisabled={!stripeStatusResolved}
          showCancel={true}
          onCancel={() => {
            history.goBack();
          }}
        />
      </div>
      <InfoModal
        isOpen={showPopup}
        title={t("Offering Created")}
        status="success"
        onDismiss={dismissOfferingCreated}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>
            {t("You created")} <b>{getValues().title}</b> {t('subscription')}.
          </div>
          {productMeta?.tax_code_label && (
            <div style={{ marginTop: 8 }}>
              {t("Assigned to Stripe category:")} <b>{productMeta.tax_code_label}</b>
              {productMeta.stripe_dashboard_url && (
                <>
                  {" "}
                  <a href={productMeta.stripe_dashboard_url} target="_blank" rel="noopener noreferrer">
                    {t("Edit in Stripe →")}
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      </InfoModal>

      <InfoModal
        isOpen={errors.length > 0}
        title={errors[0]?.details[0]?.message}
        status="error"
        onConfirm={() => {
          history.goBack();
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{t("An error occured")}</div>
        </div>
      </InfoModal>
      <BookingModal
        isOpen={showBookingModal}
        onDismiss={() => setShowBookingModal(false)}
        bookingOptions={bookingOptions}
        setBookingOptions={setBookingOptions}
        lessonRates={displayRates}
      />
    </div>
  );
};

export default Subscription;
